import React from "react";
import styled, { keyframes } from "styled-components";
import { useTranslation } from "react-i18next";

const platformTiles = [
  { id: "crm", label: { nl: "CRM", en: "CRM" }, x: 184, y: 192, icon: "people" },
  { id: "planning", label: { nl: "PLANNING", en: "PLANNING" }, x: 736, y: 192, icon: "calendar" },
  { id: "commerce", label: { nl: "WEBSHOP", en: "WEBSHOP" }, x: 184, y: 560, icon: "cart" },
  { id: "finance", label: { nl: "BOEKHOUDING", en: "ACCOUNTING" }, x: 736, y: 560, icon: "finance" },
];

export const dataPlatformFocusVisuals = ["connections", "model", "monitoring"];
export const dataPlatformMethodVisuals = ["map", "structure", "foundation", "translate"];

const dataModelGearTeeth = [0, 45, 90, 135, 180, 225, 270, 315];

const lanePaths = [
  "M260 174C326 184 356 280 414 326",
  "M260 206C336 207 356 304 414 354",
  "M260 238C328 232 352 336 414 382",
  "M660 174C594 184 564 280 506 326",
  "M660 206C584 207 564 304 506 354",
  "M660 238C592 232 568 336 506 382",
  "M260 522C326 512 356 456 414 434",
  "M260 554C336 553 356 432 414 406",
  "M260 586C328 594 352 464 414 456",
  "M660 522C594 512 564 456 506 434",
  "M660 554C584 553 564 432 506 406",
  "M660 586C592 594 568 464 506 456",
];

const backgroundTraces = [
  "M70 118H170C224 118 230 92 290 92H382",
  "M70 642H170C224 642 230 668 290 668H382",
  "M538 92H630C690 92 696 118 750 118H850",
  "M538 668H630C690 668 696 642 750 642H850",
  "M86 380H254",
  "M666 380H834",
  "M385 72V188",
  "M535 72V188",
  "M385 572V690",
  "M535 572V690",
];

const ambientSignals = [
  "M90 118H170C224 118 230 92 290 92H382",
  "M538 92H630C690 92 696 118 750 118H830",
  "M90 642H170C224 642 230 668 290 668H382",
  "M538 668H630C690 668 696 642 750 642H830",
];

const stripMediaSlotProps = (props) => {
  const nextProps = { ...props };
  ["src", "alt", "label", "hint", "aspect", "objectPosition"].forEach((key) => {
    delete nextProps[key];
  });
  return nextProps;
};

function PlatformIcon({ type }) {
  if (type === "people") {
    return (
      <g>
        <circle className="platform-icon-fill" cx="-15" cy="-8" r="10" />
        <circle className="platform-icon-fill" cx="12" cy="-10" r="12" />
        <path className="platform-icon-line" d="M-35 23C-29 8 -2 8 4 23" />
        <path className="platform-icon-line strong" d="M-8 25C-1 6 28 6 36 25" />
      </g>
    );
  }

  if (type === "calendar") {
    return (
      <g>
        <rect className="platform-icon-line" x="-34" y="-26" width="68" height="56" rx="10" />
        <path className="platform-icon-line" d="M-20 -34V-18M20 -34V-18M-34 -7H34" />
        {[0, 1, 2, 3, 4, 5].map((dot) => (
          <rect
            key={dot}
            className={dot === 1 || dot === 4 ? "calendar-cell active" : "calendar-cell"}
            x={-23 + (dot % 3) * 23}
            y={2 + Math.floor(dot / 3) * 18}
            width="9"
            height="9"
            rx="2.2"
          />
        ))}
      </g>
    );
  }

  if (type === "cart") {
    return (
      <g>
        <path className="platform-icon-line strong" d="M-39 -24H-29L-23 10H21L30 -12H-18" />
        <path className="platform-icon-line" d="M-17 -2H24" />
        <circle className="platform-icon-fill" cx="-14" cy="24" r="6" />
        <circle className="platform-icon-fill" cx="18" cy="24" r="6" />
      </g>
    );
  }

  return (
    <g>
      <ellipse className="platform-icon-line strong" cx="-12" cy="-24" rx="28" ry="10" />
      <path className="platform-icon-line strong" d="M-40 -24V14C-40 20 -27 25 -12 25C3 25 16 20 16 14V-24" />
      <path className="platform-icon-line" d="M-40 -5C-40 1 -27 6 -12 6C3 6 16 1 16 -5" />
      <path className="platform-icon-line" d="M-40 13C-40 19 -27 24 -12 24C3 24 16 19 16 13" />
      <rect className="platform-card" x="19" y="-5" width="38" height="27" rx="6" />
      <path className="platform-icon-muted" d="M27 6H47M27 15H38" />
    </g>
  );
}

function PlatformTile({ tile }) {
  return (
    <g className={`platform-tile platform-tile-${tile.id}`} data-data-platform-tile transform={`translate(${tile.x} ${tile.y})`}>
      <rect className="tile-face" x="-96" y="-82" width="192" height="140" rx="30" />
      <rect className="tile-topline" x="-64" y="-65" width="128" height="3" rx="1.5" />
      <g transform="translate(0 -10) scale(1.06)">
        <PlatformIcon type={tile.icon} />
      </g>
      <text className="platform-label" x="0" y="42">{tile.label}</text>
      <path className="tile-edge tile-edge-a" d="M-60 61H60" />
      <path className="tile-edge tile-edge-b" d="M-48 72H48" />
    </g>
  );
}

export default function DataPlatformHeroVisual({
  className,
  title = "Data platformen en koppelingen visual",
  ...frameProps
}) {
  const { i18n } = useTranslation();
  const isNl = i18n.language?.startsWith("nl");
  const labels = {
    sources: isNl ? "BRONNEN" : "SOURCES",
    output: isNl ? "UITVOER" : "OUTPUT",
    sync: "SYNC",
    reporting: isNl ? "RAPPORTAGE" : "REPORTING",
  };
  const tiles = platformTiles.map((tile) => ({
    ...tile,
    label: isNl ? tile.label.nl : tile.label.en,
  }));

  return (
    <DataPlatformFrame
      {...stripMediaSlotProps(frameProps)}
      className={className}
      data-data-platform-hero-visual
      role="img"
      aria-label={title}
    >
      <svg viewBox="0 0 920 760" focusable="false">
        <defs>
          <linearGradient id="data-platform-shell" x1="0" x2="1" y1="0" y2="1">
            <stop offset="0" stopColor="rgba(255,255,255,0.08)" />
            <stop offset="0.42" stopColor="rgba(38,54,56,0.28)" />
            <stop offset="1" stopColor="rgba(8,8,8,0.9)" />
          </linearGradient>
          <linearGradient id="data-platform-tile" x1="0" x2="1" y1="0" y2="1">
            <stop offset="0" stopColor="rgba(190,222,228,0.18)" />
            <stop offset="0.42" stopColor="rgba(255,255,255,0.035)" />
            <stop offset="1" stopColor="rgba(255,255,255,0.012)" />
          </linearGradient>
          <linearGradient id="data-platform-lane" x1="0" x2="1" y1="0" y2="0">
            <stop offset="0" stopColor="rgba(141,219,236,0.08)" />
            <stop offset="0.46" stopColor="rgba(214,238,244,0.92)" />
            <stop offset="1" stopColor="rgba(91,210,226,0.42)" />
          </linearGradient>
          <radialGradient id="data-platform-core-glow" cx="50%" cy="44%" r="64%">
            <stop offset="0" stopColor="rgba(132,229,246,0.88)" />
            <stop offset="0.34" stopColor="rgba(132,229,246,0.22)" />
            <stop offset="1" stopColor="rgba(132,229,246,0)" />
          </radialGradient>
          <filter id="data-platform-soft-glow" x="-60%" y="-60%" width="220%" height="220%">
            <feGaussianBlur stdDeviation="8" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        <rect className="visual-shell" x="12" y="14" width="896" height="732" rx="44" />
        <rect className="visual-surface" x="42" y="48" width="836" height="664" rx="36" />

        <g className="ambient-traces" aria-hidden="true">
          {backgroundTraces.map((path) => (
            <path key={path} d={path} />
          ))}
          <path d="M84 276H190C234 276 244 314 292 314H342" />
          <path d="M578 314H628C676 314 686 276 730 276H836" />
          <path d="M84 484H190C234 484 244 446 292 446H342" />
          <path d="M578 446H628C676 446 686 484 730 484H836" />
        </g>
        <g className="ambient-signals" aria-hidden="true">
          {ambientSignals.map((path, index) => (
            <path key={path} d={path} style={{ animationDelay: `${index * -1.1}s` }} />
          ))}
        </g>

        <g className="lane-group" data-data-platform-lanes>
          {lanePaths.map((path, index) => (
            <React.Fragment key={path}>
              <path className="lane-base" d={path} pathLength="100" />
              <path
                className="lane-flow"
                d={path}
                pathLength="100"
                style={{ animationDelay: `${(index % 3) * -0.74 - Math.floor(index / 3) * 0.18}s` }}
              />
            </React.Fragment>
          ))}
        </g>

        {tiles.map((tile) => (
          <PlatformTile key={tile.id} tile={tile} />
        ))}

        <g className="core" data-data-platform-core transform="translate(460 380)">
          <ellipse className="core-aura" cx="0" cy="0" rx="174" ry="150" />
          <g className="core-columns">
            {[-54, -36, -18, 0, 18, 36, 54].map((x, index) => (
              <line
                key={x}
                className="core-column"
                x1={x}
                x2={x}
                y1={-166 - (index % 3) * 12}
                y2={-86}
                style={{ animationDelay: `${index * -0.28}s` }}
              />
            ))}
          </g>
          <rect className="core-layer core-layer-3" x="-92" y="-42" width="184" height="112" rx="30" />
          <rect className="core-layer core-layer-2" x="-82" y="-56" width="164" height="112" rx="30" />
          <rect className="core-layer core-layer-1" x="-72" y="-70" width="144" height="122" rx="30" />
          <rect className="core-face" x="-62" y="-60" width="124" height="102" rx="26" />
          <path className="core-chip-line" d="M-34 6H34" />
          <path className="core-chip-line soft" d="M-24 32H24" />
          <text className="core-title" x="0" y="-11">API</text>
          <text className="core-subtitle" x="0" y="25">HUB</text>
          <g className="core-rings">
            <circle className="core-ring ring-a" cx="0" cy="0" r="88" />
            <circle className="core-ring ring-b" cx="0" cy="0" r="116" />
          </g>
        </g>

        <g className="route-labels" aria-hidden="true">
          <text x="170" y="374">{labels.sources}</text>
          <text x="750" y="374">{labels.output}</text>
          <text x="460" y="108">{labels.sync}</text>
          <text x="460" y="650">{labels.reporting}</text>
        </g>
      </svg>
    </DataPlatformFrame>
  );
}

export function DataPlatformProofVisual({
  className,
  title = "Data platform outcome visual",
  ...frameProps
}) {
  const { i18n } = useTranslation();
  const isNl = i18n.language?.startsWith("nl");
  const labels = {
    kicker: isNl ? "DATAFUNDAMENT VOOR GROEI" : "DATA FOUNDATION FOR GROWTH",
    core: isNl ? "EEN BASIS" : "ONE BASE",
    ready: isNl ? "KLAAR VOOR GROEI" : "READY TO SCALE",
  };
  const sourceRows = [
    { label: "CRM", y: 0 },
    { label: "ORDERS", y: 46 },
    { label: "INBOX", y: 92 },
  ];
  const outcomeRows = [
    { label: "DASHBOARD", y: 0, kind: "bars" },
    { label: isNl ? "INTELLIGENTIE" : "INTELLIGENCE", y: 58, kind: "ai" },
    { label: isNl ? "AUTOMATISERING" : "AUTOMATION", y: 116, kind: "gear" },
  ];
  const flowPaths = [
    "M196 132C232 132 250 150 292 158",
    "M196 178H292",
    "M196 224C232 224 250 206 292 198",
    "M408 158C448 158 458 116 496 116",
    "M408 178H496",
    "M408 198C448 198 458 250 496 250",
  ];
  const connectorNodes = [
    { x: 196, y: 132 },
    { x: 196, y: 178 },
    { x: 196, y: 224 },
    { x: 292, y: 158 },
    { x: 292, y: 178 },
    { x: 292, y: 198 },
    { x: 408, y: 158 },
    { x: 408, y: 178 },
    { x: 408, y: 198 },
    { x: 496, y: 116 },
    { x: 496, y: 178 },
    { x: 496, y: 250 },
  ];

  return (
    <DataPlatformProofFrame
      {...stripMediaSlotProps(frameProps)}
      className={className}
      data-data-platform-proof-visual
      role="img"
      aria-label={title}
    >
      <svg viewBox="0 0 720 430" focusable="false">
        <defs>
          <radialGradient id="data-proof-core-glow" cx="50%" cy="50%" r="68%">
            <stop offset="0" stopColor="rgba(126,226,242,0.45)" />
            <stop offset="0.46" stopColor="rgba(126,226,242,0.16)" />
            <stop offset="1" stopColor="rgba(126,226,242,0)" />
          </radialGradient>
          <filter id="data-proof-soft-blur" x="-80%" y="-80%" width="260%" height="260%">
            <feGaussianBlur stdDeviation="18" />
          </filter>
        </defs>
        <rect className="proof-shell" x="18" y="24" width="684" height="382" rx="34" />
        <g className="proof-background" aria-hidden="true">
          <path d="M72 216H176M538 216H652" />
        </g>

        <text className="proof-kicker" x="58" y="66">{labels.kicker}</text>

        <g data-data-platform-proof-sources transform="translate(58 116)">
          {sourceRows.map((item, index) => (
            <g key={item.label} className={`proof-source proof-source-${index + 1}`} transform={`translate(0 ${item.y})`}>
              <rect className="proof-source-card" width="138" height="32" rx="12" />
              <circle className="proof-source-dot" cx="19" cy="16" r="4.5" />
              <path className="proof-row strong" d="M36 12H80" />
              <path className="proof-row" d="M36 21H66" />
              <text className="proof-tiny proof-source-label" x="124" y="20">{item.label}</text>
            </g>
          ))}
        </g>

        <g className="proof-flow-group" data-data-platform-proof-flow>
          {flowPaths.map((path) => (
            <path key={`base-${path}`} className="proof-flow-base" d={path} pathLength="100" />
          ))}
          {connectorNodes.map((node, index) => (
            <circle
              key={`${node.x}-${node.y}-${index}`}
              className="proof-connector-node"
              cx={node.x}
              cy={node.y}
              r="3"
              style={{ animationDelay: `${(index % 6) * 0.16}s` }}
            />
          ))}
        </g>

        <g data-data-platform-proof-core transform="translate(350 178)">
          <ellipse className="proof-core-aura" cx="0" cy="0" rx="150" ry="88" />
          <ellipse className="proof-core-light" cx="0" cy="0" rx="110" ry="62" />
          <rect className="proof-core-card" x="-58" y="-58" width="116" height="104" rx="25" />
          <path className="proof-core-grid" d="M-34 -29H34M-34 -4H34M-34 21H34M-13 -43V37M13 -43V37" />
          <text className="proof-core-title" x="0" y="66">{labels.core}</text>
        </g>

        <g data-data-platform-proof-outcomes transform="translate(496 94)">
          {outcomeRows.map((item, index) => (
            <g key={item.label} className={`proof-outcome proof-outcome-${index + 1}`} transform={`translate(0 ${item.y})`}>
              <rect className="proof-outcome-card" width="166" height="44" rx="15" />
              {item.kind === "bars" ? (
                <g transform="translate(17 15)">
                  <rect className="proof-mini-bar bar-a" width="7" height="14" rx="3" />
                  <rect className="proof-mini-bar bar-b" x="13" y="-4" width="7" height="18" rx="3" />
                  <rect className="proof-mini-bar bar-c" x="26" y="3" width="7" height="11" rx="3" />
                </g>
              ) : null}
              {item.kind === "ai" ? (
                <g className="proof-ai-mark" transform="translate(16 11)">
                  <rect className="proof-ai-chip" width="36" height="22" rx="9" />
                  <path className="proof-ai-spark spark-a" d="M6 11H10" />
                  <path className="proof-ai-spark spark-b" d="M26 11H30" />
                  <text className="proof-ai-text" x="18" y="15">AI</text>
                </g>
              ) : null}
              {item.kind === "gear" ? (
                <g className="proof-gear" transform="translate(34 22)">
                  <animateTransform attributeName="transform" type="rotate" from="0 0 0" to="360 0 0" dur="9s" repeatCount="indefinite" additive="sum" />
                  {dataModelGearTeeth.map((angle) => (
                    <path key={angle} className="proof-gear-tooth" d="M-3 -15H3L4.7 -9H-4.7Z" transform={`rotate(${angle})`} />
                  ))}
                  <circle className="proof-gear-rim" r="10" />
                  <circle className="proof-gear-hole" r="5" />
                </g>
              ) : null}
              <text className="proof-outcome-label" x="70" y="26">{item.label}</text>
            </g>
          ))}
        </g>

        <g data-data-platform-proof-status transform="translate(168 310)">
          <rect className="proof-status-card" width="384" height="58" rx="19" />
          <g className="proof-status-stack" transform="translate(22 14)">
            <rect className="proof-status-layer layer-c" x="20" y="0" width="96" height="9" rx="4" />
            <rect className="proof-status-layer layer-b" x="10" y="14" width="118" height="9" rx="4" />
            <rect className="proof-status-layer layer-a" y="28" width="138" height="9" rx="4" />
          </g>
          <path className="proof-status-divider" d="M192 12V46" />
          <text className="proof-status-label" x="220" y="25">{labels.ready}</text>
          <path className="proof-status-row strong" d="M220 38H314" />
          <path className="proof-status-check" d="M336 31L342 37L354 24" />
        </g>
      </svg>
    </DataPlatformProofFrame>
  );
}

export function DataPlatformFocusVisual({ variant }) {
  return (
    <DataPlatformFocusFrame data-data-platform-focus-visual={variant}>
      <svg viewBox="0 0 360 126" focusable="false">
        <rect className="dp-focus-bg" x="8" y="8" width="344" height="110" rx="24" />

        {variant === "connections" ? (
          <>
            <g data-data-platform-source-stack transform="translate(20 23)">
              {["CRM", "PLAN", "FIN"].map((label, row) => (
                <g className={`source-row source-row-${row + 1}`} key={label} transform={`translate(0 ${row * 27})`}>
                  <rect className="source-card" width="82" height="21" rx="8" />
                  <path className="dp-muted-line" d="M12 8H42M12 14H32" />
                  <text className="source-label" x="64" y="14">{label}</text>
                </g>
              ))}
            </g>
            <rect className="api-glow" x="134" y="25" width="92" height="70" rx="28" />
            <path className="source-track" d="M104 34C123 34 133 46 146 52" />
            <path className="source-track" d="M104 88C123 88 133 74 146 68" />
            <path className="source-flow source-flow-a" d="M104 34C123 34 133 46 146 52" pathLength="100" />
            <path className="source-flow source-flow-b" d="M104 88C123 88 133 74 146 68" pathLength="100" />
            <rect className="api-orbit-track" x="146" y="26" width="68" height="68" rx="21" />
            <rect className="api-orbit-flow" x="146" y="26" width="68" height="68" rx="21" pathLength="100" />
            <g transform="translate(155 35)">
              <rect className="api-node" width="50" height="50" rx="15" />
              <text className="api-label" x="25" y="30">API</text>
            </g>
            <path className="stable-lane" d="M214 60H268" />
            <path className="stable-flow" d="M214 60H268" pathLength="100" />
            <g data-data-platform-stable-target transform="translate(268 37)">
              <ellipse className="db-line" cx="22" cy="8" rx="18" ry="7" />
              <path className="db-line" d="M4 8V36C4 40 12 44 22 44C32 44 40 40 40 36V8" />
              <path className="db-line soft" d="M4 22C4 26 12 30 22 30C32 30 40 26 40 22" />
            </g>
          </>
        ) : null}

        {variant === "model" ? (
          <>
            <g data-data-platform-model-core transform="translate(140 25)">
              <rect className="model-core" width="76" height="76" rx="21" />
              <path className="model-grid" d="M18 22H58M18 39H58M18 56H58M30 15V63M46 15V63" />
              <text className="model-label" x="38" y="86">MODEL</text>
            </g>
            <g className="model-output model-output-report" transform="translate(35 37)">
              <rect className="model-chip" width="72" height="42" rx="14" />
              <path className="model-document-icon" d="M23 10H42L49 17V33H23ZM42 10V17H49M30 22H42M30 29H45" />
            </g>
            <g className="model-output model-output-dash" transform="translate(252 23)">
              <rect className="model-chip" width="84" height="40" rx="14" />
              <path className="model-dashboard-grid" d="M14 30H70M14 20H70M24 11V31M48 11V31" />
              <path className="model-dashboard-line" d="M16 28C25 25 29 18 38 20C47 22 50 14 61 11" />
              <circle className="model-dashboard-dot" cx="61" cy="11" r="2.8" />
            </g>
            <g className="model-output model-output-auto" transform="translate(252 72)">
              <rect className="model-chip" width="84" height="40" rx="14" />
              <g className="model-gear" transform="translate(42 20)">
                <animateTransform
                  attributeName="transform"
                  type="rotate"
                  from="0 0 0"
                  to="360 0 0"
                  dur="8s"
                  repeatCount="indefinite"
                  additive="sum"
                />
                {dataModelGearTeeth.map((angle) => (
                  <path
                    key={angle}
                    className="model-gear-tooth"
                    d="M-2.8 -15.4H2.8L4.3 -9.9H-4.3Z"
                    transform={`rotate(${angle})`}
                  />
                ))}
                <circle className="model-gear-rim" cx="0" cy="0" r="10.7" />
                <circle className="model-gear-inner" cx="0" cy="0" r="6" />
              </g>
            </g>
            <path className="model-link" d="M140 58H107" />
            <path className="model-link" d="M216 48H252" />
            <path className="model-link" d="M216 76C232 76 236 92 252 92" />
            <path className="model-sync-flow" d="M140 58H107" pathLength="100" />
            <path className="model-sync-flow" d="M216 48H252" pathLength="100" />
            <path className="model-sync-flow" d="M216 76C232 76 236 92 252 92" pathLength="100" />
          </>
        ) : null}

        {variant === "monitoring" ? (
          <>
            <g data-data-platform-monitor transform="translate(36 18)">
              <rect className="monitor-screen" width="150" height="72" rx="18" />
              <path className="monitor-bpm-base" d="M18 43H49L60 31L70 56L84 19L96 53L108 35L118 43" />
              <path className="monitor-bpm-flow" d="M18 43H49L60 31L70 56L84 19L96 53L108 35L118 43" pathLength="100" />
              <path className="monitor-stand" d="M76 72V88M55 88H97" />
            </g>
            <g className="monitor-lens" transform="translate(170 55)">
              <circle className="monitor-lens-ring" cx="0" cy="0" r="25" />
              <path className="monitor-lens-shine" d="M-11 -7C-5 -13 6 -13 12 -7" />
              <path className="monitor-lens-handle" d="M17 18L36 37" />
              <circle className="monitor-lens-glow" cx="-6" cy="-4" r="3.2" />
            </g>
            <path className="handover-link handover-link-monitor" d="M209 60C220 60 225 56 236 56H256" />
            <path className="handover-flow handover-flow-monitor" d="M209 60C220 60 225 56 236 56H256" pathLength="100" />
            <g className="handover-output" data-data-platform-status-output transform="translate(256 29)">
              <rect className="status-board" width="80" height="62" rx="17" />
              <path className="monitor-sweep" d="M14 12H66" />
              <path className="monitor-line ok" d="M16 23H49" />
              <path className="monitor-line ok" d="M16 36H54" />
              <path className="monitor-line alert" d="M16 49H43" />
              <circle className="monitor-dot ok" cx="58" cy="23" r="4" />
              <circle className="monitor-dot ok" cx="64" cy="36" r="4" />
              <circle className="monitor-dot warning" cx="52" cy="49" r="5" />
            </g>
          </>
        ) : null}
      </svg>
    </DataPlatformFocusFrame>
  );
}

export function DataPlatformMethodVisual({ variant }) {
  return (
    <DataPlatformMethodFrame data-data-platform-method-visual={variant} aria-hidden="true">
      <svg viewBox="0 0 240 118" focusable="false">
        <rect className="dp-method-bg" x="1" y="1" width="238" height="116" rx="23" />

        {variant === "map" ? (
          <>
            <g data-data-platform-method-map transform="translate(16 26)">
              {[0, 1, 2].map((row) => (
                <g key={row} className={`map-source map-source-${row + 1}`} transform={`translate(0 ${row * 24})`}>
                  <rect className="map-source-card" width="66" height="18" rx="7" />
                  <rect className="map-source-dot" x="9" y="6" width="6" height="6" rx="2" />
                  <path className="map-source-line" d="M22 6H47M22 12H39" />
                </g>
              ))}
              <path className="map-route route-a" d="M68 9C82 9 88 18 94 24" />
              <path className="map-route route-b" d="M68 33H92" />
              <path className="map-route route-c" d="M68 57C82 57 88 48 94 42" />
              <g className="map-lens" transform="translate(116 33)">
                <circle className="map-lens-fill" r="25" />
                <circle className="map-lens-track" r="24" />
                <circle className="map-lens-flow" r="24" pathLength="100" />
                <path className="map-lens-handle" d="M17 18L33 34" />
                <path className="map-lens-scan" d="M-12 -7C-5 -13 7 -13 13 -6" />
              </g>
              <g transform="translate(158 12)">
                <rect className="map-target-card" width="54" height="42" rx="13" />
                <path className="map-source-line strong" d="M13 14H41M13 25H33" />
                <circle className="map-target-status" cx="42" cy="29" r="4" />
              </g>
            </g>
          </>
        ) : null}

        {variant === "structure" ? (
          <>
            <g data-data-platform-method-structure transform="translate(12 20)">
              <g className="structure-inputs">
                <rect className="structure-piece piece-a" x="0" y="8" width="56" height="18" rx="7" />
                <rect className="structure-piece piece-b" x="0" y="32" width="56" height="18" rx="7" />
                <rect className="structure-piece piece-c" x="0" y="56" width="56" height="18" rx="7" />
                <path className="structure-line" d="M11 17H37M11 41H45M11 65H31" />
              </g>
              <g className="structure-blueprint" transform="translate(82 2)">
                <rect className="structure-core" width="76" height="76" rx="19" />
                <path className="structure-grid" d="M38 11V65M12 38H64" />
                <rect className="structure-slot active slot-a" x="15" y="15" width="18" height="15" rx="4.5" />
                <rect className="structure-slot slot-b" x="43" y="15" width="18" height="15" rx="4.5" />
                <rect className="structure-slot slot-c" x="15" y="46" width="18" height="15" rx="4.5" />
                <rect className="structure-slot active slot-d" x="43" y="46" width="18" height="15" rx="4.5" />
              </g>
              <g transform="translate(176 21)">
                <rect className="structure-output" width="46" height="38" rx="13" />
                <path className="structure-output-line" d="M12 13H32M12 24H26" />
                <circle className="structure-output-dot" cx="34" cy="24" r="3.5" />
              </g>
              <path className="structure-connector" d="M58 41H78" />
              <path className="structure-connector" d="M162 41H172" />
              <path className="structure-flow flow-left" d="M58 41H78" pathLength="100" />
              <path className="structure-flow flow-right" d="M162 41H172" pathLength="100" />
            </g>
          </>
        ) : null}

        {variant === "foundation" ? (
          <>
            <g data-data-platform-method-foundation transform="translate(20 18)">
              <g className="foundation-stack" transform="translate(0 12)">
                <rect className="foundation-layer layer-c" x="18" y="0" width="62" height="13" rx="4.5" />
                <rect className="foundation-layer layer-b" x="8" y="18" width="82" height="13" rx="4.5" />
                <rect className="foundation-layer layer-a" x="0" y="36" width="100" height="13" rx="4.5" />
                <path className="foundation-column" d="M26 0V49M74 0V49" />
              </g>
              <path className="foundation-link" d="M105 38H122" />
              <path className="foundation-flow" d="M105 38H122" pathLength="100" />
              <g className="foundation-status" transform="translate(124 6)">
                <rect className="foundation-status-card" width="82" height="60" rx="17" />
                <path className="foundation-status-line strong" d="M15 18H56" />
                <path className="foundation-status-line" d="M15 31H62" />
                <path className="foundation-status-line" d="M15 44H48" />
                <circle className="foundation-status-dot dot-a" cx="66" cy="18" r="4" />
                <circle className="foundation-status-dot dot-b" cx="66" cy="31" r="4" />
                <circle className="foundation-status-dot dot-c" cx="52" cy="44" r="4" />
                <path className="foundation-ready" d="M62 45L67 50L76 38" />
              </g>
            </g>
          </>
        ) : null}

        {variant === "translate" ? (
          <>
            <g data-data-platform-method-translate transform="translate(20 18)">
              <g className="translate-foundation" transform="translate(4 24)">
                <rect className="translate-base" width="74" height="44" rx="15" />
                <path className="translate-base-line strong" d="M15 16H56" />
                <path className="translate-base-line" d="M15 28H47" />
                <circle className="translate-base-dot" cx="58" cy="28" r="4" />
              </g>
              <g className="translate-output output-dashboard" transform="translate(116 4)">
                <rect className="translate-output-card" width="80" height="34" rx="13" />
                <rect className="translate-bar bar-a" x="15" y="19" width="8" height="8" rx="3" />
                <rect className="translate-bar bar-b" x="31" y="11" width="8" height="16" rx="3" />
                <rect className="translate-bar bar-c" x="47" y="15" width="8" height="12" rx="3" />
                <path className="translate-output-line" d="M63 11H75M63 22H71" />
              </g>
              <g className="translate-output output-ai" transform="translate(116 50)">
                <rect className="translate-output-card" width="80" height="34" rx="13" />
                <rect className="translate-ai-chip" x="14" y="7" width="27" height="19" rx="8" />
                <text className="translate-ai-label" x="27.5" y="20.7">AI</text>
                <path className="translate-output-line" d="M55 11H74M55 22H67" />
              </g>
              <path className="translate-link link-top" d="M80 46C95 46 99 21 116 21" />
              <path className="translate-link link-bottom" d="M80 46C95 46 99 67 116 67" />
              <path className="translate-pulse link-top" d="M80 46C95 46 99 21 116 21" pathLength="100" />
              <path className="translate-pulse link-bottom" d="M80 46C95 46 99 67 116 67" pathLength="100" />
            </g>
          </>
        ) : null}
      </svg>
    </DataPlatformMethodFrame>
  );
}

const laneMove = keyframes`
  to {
    stroke-dashoffset: -100;
  }
`;

const coreBreathe = keyframes`
  0%, 100% {
    opacity: 0.5;
    transform: scale(0.94);
  }

  50% {
    opacity: 0.95;
    transform: scale(1.05);
  }
`;

const columnRise = keyframes`
  0% {
    stroke-dashoffset: 68;
    opacity: 0;
  }

  20%, 74% {
    opacity: 0.84;
  }

  100% {
    stroke-dashoffset: -68;
    opacity: 0;
  }
`;

const coreSpin = keyframes`
  to {
    transform: rotate(360deg);
  }
`;

const edgeScan = keyframes`
  0%, 100% {
    stroke-dashoffset: 94;
  }

  50% {
    stroke-dashoffset: 0;
  }
`;

const ambientTrace = keyframes`
  to {
    stroke-dashoffset: -120;
  }
`;

const focusStableFlow = keyframes`
  to {
    stroke-dashoffset: -100;
  }
`;

const focusCardWake = keyframes`
  0%, 100% {
    opacity: 0.66;
  }

  45%, 62% {
    opacity: 1;
  }
`;

const focusWarningPulse = keyframes`
  0%, 100% {
    opacity: 0.62;
    transform: scale(1);
  }

  45%, 60% {
    opacity: 1;
    transform: scale(1.18);
  }
`;

const focusSweep = keyframes`
  to {
    stroke-dashoffset: -78;
  }
`;

const methodLensOrbit = keyframes`
  to {
    stroke-dashoffset: -100;
  }
`;

const methodSoftWake = keyframes`
  0%, 100% {
    opacity: 0.68;
    filter: drop-shadow(0 0 0 rgba(123, 210, 236, 0));
  }

  48%, 62% {
    opacity: 1;
    filter: drop-shadow(0 0 8px rgba(123, 210, 236, 0.24));
  }
`;

const methodLayerWake = keyframes`
  0%, 100% {
    opacity: 0.58;
  }

  38%, 68% {
    opacity: 0.94;
  }
`;

const methodShortFlow = keyframes`
  to {
    stroke-dashoffset: -100;
  }
`;

const proofGlow = keyframes`
  0%, 100% {
    opacity: 0.66;
    filter: drop-shadow(0 0 0 rgba(123, 210, 236, 0));
  }

  48%, 62% {
    opacity: 1;
    filter: drop-shadow(0 0 10px rgba(123, 210, 236, 0.28));
  }
`;

const proofNodePulse = keyframes`
  0%, 100% {
    opacity: 0.24;
    transform: scale(0.7);
  }

  44%, 58% {
    opacity: 0.92;
    transform: scale(1.32);
  }
`;

const proofAiPulse = keyframes`
  0%, 100% {
    opacity: 0.72;
    filter: drop-shadow(0 0 0 rgba(123, 210, 236, 0));
  }

  46%, 62% {
    opacity: 1;
    filter: drop-shadow(0 0 9px rgba(123, 210, 236, 0.32));
  }
`;

const proofLayerPulse = keyframes`
  0%, 100% {
    opacity: 0.62;
    transform: scaleY(0.92);
  }

  48%, 62% {
    opacity: 0.94;
    transform: scaleY(1);
  }
`;

const DataPlatformProofFrame = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 16 / 10;
  overflow: hidden;
  border-radius: 24px;
  background:
    radial-gradient(ellipse 34% 23% at 50% 42%, rgba(123, 210, 236, 0.12), transparent 72%),
    radial-gradient(circle at 12% 20%, rgba(255, 255, 255, 0.08), transparent 34%),
    radial-gradient(circle at 88% 76%, rgba(255, 255, 255, 0.06), transparent 36%),
    linear-gradient(145deg, rgba(255, 255, 255, 0.05), rgba(255, 255, 255, 0.014)),
    #070808;
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.07),
    inset 0 -28px 70px rgba(0, 0, 0, 0.24),
    0 24px 70px rgba(0, 0, 0, 0.28);

  svg {
    width: 100%;
    height: 100%;
    display: block;
  }

  .proof-shell {
    fill: rgba(255, 255, 255, 0.018);
    stroke: rgba(255, 255, 255, 0.12);
    stroke-width: 1.25;
  }

  .proof-background path {
    fill: none;
    stroke: rgba(205, 235, 243, 0.075);
    stroke-width: 1.7;
    stroke-linecap: round;
    stroke-linejoin: round;
  }

  .proof-kicker,
  .proof-tiny,
  .proof-core-title,
  .proof-outcome-label,
  .proof-status-label {
    font-family: inherit;
    font-weight: 850;
    letter-spacing: 0.14em;
    text-transform: uppercase;
  }

  .proof-kicker {
    fill: rgba(255, 255, 255, 0.52);
    font-size: 12px;
  }

  .proof-tiny {
    fill: rgba(235, 246, 248, 0.62);
    font-size: 7.6px;
  }

  .proof-source-label {
    text-anchor: end;
  }

  .proof-source-card,
  .proof-outcome-card,
  .proof-core-card,
  .proof-status-card,
  .proof-ai-chip {
    fill: rgba(255, 255, 255, 0.03);
    stroke: rgba(205, 235, 243, 0.18);
    stroke-width: 1.15;
  }

  .proof-source-card,
  .proof-outcome-card,
  .proof-core-card,
  .proof-status-card {
    fill: rgba(123, 210, 236, 0.045);
  }

  .proof-core-aura {
    fill: url("#data-proof-core-glow");
    filter: url("#data-proof-soft-blur");
    opacity: 0.74;
    animation: ${coreBreathe} 5.6s ease-in-out infinite;
    transform-box: fill-box;
    transform-origin: center;
  }

  .proof-core-light {
    fill: url("#data-proof-core-glow");
    filter: url("#data-proof-soft-blur");
    opacity: 0.36;
  }

  .proof-row,
  .proof-flow-base,
  .proof-core-grid,
  .proof-status-divider,
  .proof-status-row,
  .proof-ai-spark,
  .proof-status-check {
    fill: none;
    stroke-linecap: round;
    stroke-linejoin: round;
  }

  .proof-row,
  .proof-status-row {
    stroke: rgba(170, 195, 202, 0.5);
    stroke-width: 2.1;
  }

  .proof-row.strong,
  .proof-status-row.strong {
    stroke: rgba(205, 235, 243, 0.75);
    stroke-width: 2.6;
  }

  .proof-source-dot,
  .proof-status-dot {
    fill: rgba(123, 210, 236, 0.84);
    filter: drop-shadow(0 0 7px rgba(123, 210, 236, 0.3));
    animation: ${proofGlow} 4.2s ease-in-out infinite;
  }

  .proof-source-2,
  .proof-outcome-2 {
    animation-delay: 0.22s;
  }

  .proof-source-3,
  .proof-outcome-3 {
    animation-delay: 0.44s;
  }

  .proof-flow-base {
    stroke: rgba(205, 235, 243, 0.16);
    stroke-width: 2.8;
  }

  .proof-connector-node {
    fill: rgba(215, 244, 250, 0.82);
    transform-box: fill-box;
    transform-origin: center;
    animation: ${proofNodePulse} 4.6s ease-in-out infinite;
    filter: drop-shadow(0 0 7px rgba(123, 210, 236, 0.32));
  }

  .proof-core-grid {
    stroke: rgba(205, 235, 243, 0.54);
    stroke-width: 2.7;
  }

  .proof-core-title {
    fill: rgba(230, 248, 252, 0.82);
    font-size: 10px;
    text-anchor: middle;
  }

  .proof-outcome {
    animation: ${proofGlow} 5.4s ease-in-out infinite;
  }

  .proof-mini-bar,
  .proof-status-layer {
    fill: rgba(123, 210, 236, 0.58);
    transform-box: fill-box;
    transform-origin: center bottom;
    animation: ${proofLayerPulse} 4.6s ease-in-out infinite;
  }

  .bar-b,
  .layer-b {
    animation-delay: 0.2s;
  }

  .bar-c,
  .layer-c {
    animation-delay: 0.4s;
  }

  .proof-ai-chip {
    stroke: rgba(205, 235, 243, 0.28);
  }

  .proof-ai-mark {
    animation: ${proofAiPulse} 3.4s ease-in-out infinite;
  }

  .proof-ai-spark {
    stroke: rgba(207, 238, 244, 0.68);
    stroke-width: 1.4;
    opacity: 0.58;
  }

  .proof-ai-text,
  .proof-outcome-label,
  .proof-status-label {
    fill: rgba(230, 248, 252, 0.82);
    font-size: 8.2px;
  }

  .proof-ai-text {
    font-size: 9.8px;
    font-weight: 850;
    text-anchor: middle;
    letter-spacing: 0.08em;
  }

  .proof-gear-tooth,
  .proof-gear-rim {
    fill: rgba(123, 210, 236, 0.12);
    stroke: rgba(205, 235, 243, 0.72);
    stroke-width: 1.25;
    stroke-linejoin: round;
  }

  .proof-gear-hole {
    fill: rgba(7, 12, 13, 0.9);
    stroke: rgba(205, 235, 243, 0.54);
    stroke-width: 1.1;
  }

  .proof-status-divider {
    stroke: rgba(205, 235, 243, 0.12);
  }

  .proof-status-check {
    stroke: rgba(230, 248, 252, 0.9);
    stroke-width: 2.55;
    animation: ${proofGlow} 4.2s ease-in-out infinite;
  }

  @media (prefers-reduced-motion: reduce) {
    .proof-core-aura,
    .proof-connector-node,
    .proof-source-dot,
    .proof-status-dot,
    .proof-outcome,
    .proof-ai-mark,
    .proof-mini-bar,
    .proof-status-layer,
    .proof-status-check {
      animation: none !important;
    }
  }
`;

const DataPlatformMethodFrame = styled.div`
  width: 100%;
  aspect-ratio: 240 / 118;
  overflow: hidden;
  border-radius: 20px;
  margin-bottom: clamp(6px, 1vw, 10px);
  background:
    radial-gradient(circle at 50% 50%, rgba(123, 210, 236, 0.1), transparent 42%),
    linear-gradient(145deg, rgba(255, 255, 255, 0.046), rgba(255, 255, 255, 0.014));

  svg {
    width: 100%;
    height: 100%;
    display: block;
  }

  .dp-method-bg,
  .map-source-card,
  .map-target-card,
  .structure-piece,
  .structure-core,
  .structure-output,
  .foundation-status-card,
  .translate-base,
  .translate-output-card,
  .translate-ai-chip {
    fill: rgba(255, 255, 255, 0.032);
    stroke: rgba(255, 255, 255, 0.15);
    stroke-width: 1.2;
  }

  .dp-method-bg {
    fill: rgba(255, 255, 255, 0.022);
    stroke: rgba(255, 255, 255, 0.135);
  }

  .map-target-card,
  .structure-core,
  .foundation-status-card,
  .translate-base,
  .translate-output-card {
    fill: rgba(123, 210, 236, 0.07);
    stroke: rgba(205, 235, 243, 0.24);
    filter: drop-shadow(0 0 10px rgba(123, 210, 236, 0.09));
  }

  .map-source-line,
  .map-route,
  .map-lens-track,
  .map-lens-flow,
  .map-lens-handle,
  .map-lens-scan,
  .structure-line,
  .structure-grid,
  .structure-output-line,
  .structure-connector,
  .structure-flow,
  .foundation-column,
  .foundation-status-line,
  .foundation-link,
  .foundation-flow,
  .foundation-ready,
  .translate-base-line,
  .translate-output-line,
  .translate-link,
  .translate-pulse {
    fill: none;
    stroke-linecap: round;
    stroke-linejoin: round;
  }

  .map-source-line,
  .structure-line,
  .structure-output-line,
  .foundation-status-line,
  .translate-base-line,
  .translate-output-line {
    stroke: rgba(170, 195, 202, 0.54);
    stroke-width: 2.5;
  }

  .map-source-line.strong,
  .foundation-status-line.strong,
  .translate-base-line.strong {
    stroke: rgba(205, 235, 243, 0.78);
    stroke-width: 3.2;
  }

  .map-source {
    animation: ${methodSoftWake} 5s ease-in-out infinite;
  }

  .map-source-2 {
    animation-delay: 0.22s;
  }

  .map-source-3 {
    animation-delay: 0.44s;
  }

  .map-source-dot,
  .map-target-status,
  .structure-output-dot,
  .foundation-status-dot,
  .translate-base-dot {
    fill: rgba(123, 210, 236, 0.78);
    filter: drop-shadow(0 0 5px rgba(123, 210, 236, 0.22));
  }

  .map-route,
  .structure-connector,
  .foundation-link,
  .translate-link {
    stroke: rgba(205, 235, 243, 0.2);
    stroke-width: 2.35;
  }

  .map-lens-fill {
    fill: rgba(123, 210, 236, 0.04);
  }

  .map-lens-track {
    stroke: rgba(205, 235, 243, 0.22);
    stroke-width: 2.2;
  }

  .map-lens-flow {
    fill: none;
    stroke: rgba(205, 235, 243, 0.82);
    stroke-width: 2.8;
    stroke-dasharray: 28 72;
    animation: ${methodLensOrbit} 3.4s linear infinite;
    filter: drop-shadow(0 0 6px rgba(123, 210, 236, 0.24));
  }

  .map-lens-handle {
    stroke: rgba(205, 235, 243, 0.6);
    stroke-width: 4;
  }

  .map-lens-scan {
    stroke: rgba(225, 245, 250, 0.84);
    stroke-width: 3;
    animation: ${methodSoftWake} 2.8s ease-in-out infinite;
  }

  .structure-slot {
    fill: rgba(205, 235, 243, 0.12);
    stroke: rgba(205, 235, 243, 0.18);
    stroke-width: 1;
  }

  .structure-slot.active,
  .translate-bar,
  .foundation-layer {
    fill: rgba(123, 210, 236, 0.55);
  }

  .structure-grid {
    stroke: rgba(205, 235, 243, 0.34);
    stroke-width: 2;
  }

  .structure-output,
  .structure-core {
    animation: ${methodSoftWake} 4.8s ease-in-out infinite;
  }

  .structure-flow {
    stroke: rgba(225, 245, 250, 0.82);
    stroke-width: 2.8;
    stroke-dasharray: 28 72;
    animation: ${methodShortFlow} 3s linear infinite;
    filter: drop-shadow(0 0 5px rgba(123, 210, 236, 0.24));
  }

  .flow-right {
    animation-delay: -0.35s;
  }

  .structure-slot.active {
    animation: ${methodSoftWake} 3.7s ease-in-out infinite;
  }

  .slot-d {
    animation-delay: 0.28s;
  }

  .structure-output {
    animation-delay: 0.34s;
  }

  .foundation-layer {
    stroke: rgba(205, 235, 243, 0.2);
    stroke-width: 1;
    animation: ${methodLayerWake} 4.2s ease-in-out infinite;
  }

  .layer-b {
    animation-delay: 0.2s;
  }

  .layer-c {
    animation-delay: 0.4s;
  }

  .foundation-column {
    stroke: rgba(205, 235, 243, 0.24);
    stroke-width: 2.2;
  }

  .foundation-flow {
    stroke: rgba(225, 245, 250, 0.84);
    stroke-width: 2.8;
    stroke-dasharray: 22 78;
    animation: ${methodShortFlow} 3.1s linear infinite;
    filter: drop-shadow(0 0 5px rgba(123, 210, 236, 0.24));
  }

  .foundation-status-dot {
    animation: ${methodSoftWake} 3.2s ease-in-out infinite;
  }

  .dot-b {
    animation-delay: 0.22s;
  }

  .dot-c {
    animation-delay: 0.44s;
  }

  .foundation-ready {
    stroke: rgba(225, 245, 250, 0.88);
    stroke-width: 3.1;
    animation: ${methodSoftWake} 3.6s ease-in-out infinite;
  }

  .translate-output {
    animation: ${methodSoftWake} 5s ease-in-out infinite;
  }

  .output-ai {
    animation-delay: 0.42s;
  }

  .translate-bar {
    opacity: 0.78;
    animation: ${methodLayerWake} 3.8s ease-in-out infinite;
  }

  .bar-b {
    animation-delay: 0.22s;
  }

  .bar-c {
    animation-delay: 0.44s;
  }

  .translate-ai-label {
    fill: rgba(230, 248, 252, 0.86);
    font-size: 11px;
    font-weight: 800;
    letter-spacing: 0.04em;
    text-anchor: middle;
  }

  .translate-pulse {
    stroke: rgba(225, 245, 250, 0.82);
    stroke-width: 2.4;
    stroke-dasharray: 12 88;
    animation: ${methodShortFlow} 3.8s linear infinite;
    filter: drop-shadow(0 0 5px rgba(123, 210, 236, 0.22));
  }

  .link-bottom {
    animation-delay: -0.38s;
  }

  @media (prefers-reduced-motion: reduce) {
    .map-source,
    .map-lens-flow,
    .map-lens-scan,
    .structure-output,
    .structure-core,
    .structure-flow,
    .structure-slot.active,
    .foundation-layer,
    .foundation-flow,
    .foundation-status-dot,
    .foundation-ready,
    .translate-output,
    .translate-bar,
    .translate-pulse {
      animation: none !important;
      opacity: 1;
    }
  }
`;

const DataPlatformFocusFrame = styled.div`
  width: 100%;
  aspect-ratio: 360 / 126;
  overflow: hidden;
  border-radius: 20px;
  margin-bottom: clamp(4px, 0.8vw, 8px);
  background:
    radial-gradient(circle at 50% 50%, rgba(123, 210, 236, 0.12), transparent 38%),
    linear-gradient(145deg, rgba(255, 255, 255, 0.05), rgba(255, 255, 255, 0.014));

  svg {
    width: 100%;
    height: 100%;
    display: block;
  }

  .dp-focus-bg,
  .source-card,
  .api-node,
  .model-core,
  .model-chip,
  .monitor-card,
  .monitor-screen,
  .handover-card,
  .status-board,
  .status-card {
    fill: rgba(255, 255, 255, 0.032);
    stroke: rgba(255, 255, 255, 0.15);
    stroke-width: 1.2;
  }

  .dp-focus-bg {
    fill: rgba(255, 255, 255, 0.014);
    stroke: rgba(255, 255, 255, 0.11);
  }

  .api-node,
  .model-core,
  .handover-card,
  .monitor-screen,
  .status-board {
    fill: rgba(123, 210, 236, 0.085);
    stroke: rgba(123, 210, 236, 0.28);
    filter: drop-shadow(0 0 14px rgba(123, 210, 236, 0.12));
  }

  .dp-muted-line,
  .broken-export,
  .source-track,
  .source-flow,
  .api-orbit-track,
  .api-orbit-flow,
  .stable-lane,
  .stable-flow,
  .db-line,
  .model-grid,
  .dp-icon-line,
  .model-document-icon,
  .model-dashboard-grid,
  .model-dashboard-line,
  .model-link,
  .model-sync-flow,
  .monitor-line,
  .monitor-sweep,
  .monitor-bpm-base,
  .monitor-bpm-flow,
  .monitor-stand,
  .monitor-lens-ring,
  .monitor-lens-shine,
  .monitor-lens-handle,
  .handover-icon,
  .status-link,
  .handover-link,
  .handover-flow,
  .status-check,
  .status-small-line {
    fill: none;
    stroke-linecap: round;
    stroke-linejoin: round;
  }

  .dp-muted-line,
  .model-grid,
  .dp-icon-line,
  .handover-icon,
  .status-small-line {
    stroke: rgba(205, 235, 243, 0.62);
    stroke-width: 2.4;
  }

  .broken-export,
  .source-track,
  .api-orbit-track,
  .model-link,
  .status-link,
  .handover-link {
    stroke: rgba(255, 255, 255, 0.16);
    stroke-width: 2.2;
  }

  .api-glow {
    fill: rgba(123, 210, 236, 0.1);
    filter: blur(15px);
    opacity: 0.58;
  }

  .source-track,
  .api-orbit-track,
  .stable-lane {
    stroke: rgba(205, 235, 243, 0.18);
    stroke-width: 2.4;
  }

  .api-orbit-track {
    stroke: rgba(123, 210, 236, 0.18);
  }

  .stable-flow,
  .source-flow,
  .api-orbit-flow,
  .model-sync-flow,
  .handover-flow {
    stroke: rgba(205, 235, 243, 0.86);
    stroke-width: 3;
  }

  .stable-flow,
  .source-flow,
  .api-orbit-flow,
  .model-sync-flow,
  .handover-flow {
    stroke-dasharray: 16 84;
    animation: ${focusStableFlow} 3.8s linear infinite;
    filter: drop-shadow(0 0 6px rgba(123, 210, 236, 0.26));
  }

  .source-flow,
  .api-orbit-flow {
    stroke: rgba(205, 235, 243, 0.9);
  }

  .source-flow-b {
    animation-delay: -0.18s;
  }

  .api-orbit-flow {
    fill: none;
    stroke: rgba(123, 210, 236, 0.86);
    stroke-dasharray: 34 66;
    animation-duration: 4.6s;
  }

  .stable-flow {
    animation-delay: -0.44s;
  }

  .handover-link-monitor {
    stroke-width: 2.4;
  }

  .handover-flow-monitor {
    stroke-dasharray: 38 62;
    stroke-width: 3.2;
    animation-duration: 3.2s;
  }

  .source-row {
    animation: ${focusCardWake} 4.6s ease-in-out infinite;
  }

  .source-row-2 {
    animation-delay: 0.18s;
  }

  .source-row-3 {
    animation-delay: 0.36s;
  }

  .source-label,
  .api-label,
  .model-label,
  .model-chip-label {
    fill: rgba(236, 246, 248, 0.76);
    font-size: 9px;
    font-weight: 800;
    letter-spacing: 0.12em;
    text-anchor: middle;
  }

  .source-label {
    fill: rgba(255, 255, 255, 0.42);
    font-size: 8px;
    letter-spacing: 0.09em;
    text-anchor: middle;
  }

  .api-label {
    fill: rgba(230, 248, 252, 0.9);
    font-size: 13px;
  }

  .db-line {
    stroke: rgba(205, 235, 243, 0.76);
    stroke-width: 2.6;
  }

  .db-line.soft {
    stroke: rgba(123, 210, 236, 0.58);
  }

  .model-output {
    animation: ${focusCardWake} 5s ease-in-out infinite;
  }

  .model-output:nth-of-type(2) {
    animation-delay: 0.2s;
  }

  .model-output:nth-of-type(3) {
    animation-delay: 0.4s;
  }

  .model-gear {
    filter: drop-shadow(0 0 5px rgba(123, 210, 236, 0.22));
  }

  .model-document-icon,
  .model-dashboard-grid,
  .model-dashboard-line {
    fill: none;
    stroke: rgba(205, 235, 243, 0.7);
    stroke-width: 2;
    stroke-linejoin: round;
  }

  .model-document-icon {
    stroke-width: 2.1;
  }

  .model-dashboard-grid {
    opacity: 0.32;
    stroke-width: 1.4;
  }

  .model-dashboard-line {
    stroke: rgba(205, 235, 243, 0.82);
    stroke-width: 2.4;
  }

  .model-dashboard-dot {
    fill: rgba(123, 210, 236, 0.86);
    filter: drop-shadow(0 0 6px rgba(123, 210, 236, 0.25));
  }

  .model-gear-tooth,
  .model-gear-rim {
    fill: rgba(123, 210, 236, 0.12);
    stroke: rgba(205, 235, 243, 0.72);
    stroke-width: 1.4;
    stroke-linejoin: round;
  }

  .model-gear-inner {
    fill: rgba(12, 23, 26, 0.82);
    stroke: rgba(205, 235, 243, 0.52);
    stroke-width: 1.2;
  }

  .monitor-screen {
    fill: rgba(255, 255, 255, 0.026);
    stroke: rgba(205, 235, 243, 0.22);
  }

  .monitor-bpm-base {
    stroke: rgba(205, 235, 243, 0.24);
    stroke-width: 3.2;
  }

  .monitor-bpm-flow {
    stroke: rgba(205, 235, 243, 0.9);
    stroke-width: 3.2;
    stroke-dasharray: 42 58;
    animation: ${focusStableFlow} 3.35s linear infinite;
    filter: drop-shadow(0 0 8px rgba(123, 210, 236, 0.28));
  }

  .monitor-stand {
    stroke: rgba(205, 235, 243, 0.48);
    stroke-width: 2.4;
  }

  .monitor-lens {
    filter: drop-shadow(0 0 12px rgba(123, 210, 236, 0.14));
  }

  .monitor-lens-ring {
    fill: rgba(123, 210, 236, 0.035);
    stroke: rgba(205, 235, 243, 0.7);
    stroke-width: 2.6;
  }

  .monitor-lens-shine {
    stroke: rgba(205, 235, 243, 0.64);
    stroke-width: 2.3;
  }

  .monitor-lens-handle {
    stroke: rgba(205, 235, 243, 0.72);
    stroke-width: 4;
  }

  .monitor-lens-glow {
    fill: rgba(123, 210, 236, 0.92);
    filter: drop-shadow(0 0 7px rgba(123, 210, 236, 0.38));
  }

  .monitor-line.ok {
    stroke: rgba(205, 235, 243, 0.54);
    stroke-width: 3;
  }

  .monitor-line.alert {
    stroke: rgba(123, 210, 236, 0.86);
    stroke-width: 3.2;
  }

  .monitor-dot {
    fill: rgba(123, 210, 236, 0.74);
  }

  .monitor-dot.warning {
    fill: rgba(230, 248, 252, 0.92);
    transform-box: fill-box;
    transform-origin: center;
    animation: ${focusWarningPulse} 2.8s ease-in-out infinite;
    filter: drop-shadow(0 0 8px rgba(123, 210, 236, 0.36));
  }

  .monitor-sweep {
    stroke: rgba(230, 248, 252, 0.86);
    stroke-width: 2.4;
    stroke-dasharray: 22 56;
    animation: ${focusSweep} 2.8s linear infinite;
    filter: drop-shadow(0 0 7px rgba(123, 210, 236, 0.26));
  }

  .status-check {
    stroke: rgba(230, 248, 252, 0.9);
    stroke-width: 3.6;
  }

  .status-check.compact {
    stroke-width: 2.8;
  }

  .handover-output {
    animation: ${focusCardWake} 4.8s ease-in-out infinite;
  }

  @media (prefers-reduced-motion: reduce) {
    .stable-flow,
    .source-flow,
    .api-orbit-flow,
    .model-sync-flow,
    .handover-flow,
    .source-row,
    .model-output,
    .model-gear,
    .monitor-bpm-flow,
    .handover-output,
    .monitor-dot.warning,
    .monitor-sweep {
      animation: none;
    }
  }
`;

const DataPlatformFrame = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 16 / 14;
  overflow: hidden;
  border-radius: 24px;
  border: 0;
  background:
    radial-gradient(circle at 50% 46%, rgba(96, 190, 204, 0.18), transparent 35%),
    radial-gradient(circle at 14% 15%, rgba(255, 255, 255, 0.09), transparent 32%),
    radial-gradient(circle at 86% 82%, rgba(255, 255, 255, 0.08), transparent 34%),
    #070808;
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.07),
    inset 0 -34px 80px rgba(0, 0, 0, 0.26),
    0 28px 80px rgba(0, 0, 0, 0.34);

  svg {
    display: block;
    width: 100%;
    height: 100%;
  }

  .visual-shell {
    fill: url("#data-platform-shell");
    stroke: rgba(255, 255, 255, 0.14);
    stroke-width: 1.4;
  }

  .visual-surface {
    fill: rgba(255, 255, 255, 0.018);
  }

  .ambient-traces path,
  .ambient-signals path {
    fill: none;
    stroke-linecap: round;
    stroke-linejoin: round;
  }

  .ambient-traces path {
    stroke: rgba(207, 236, 241, 0.08);
    stroke-width: 2;
  }

  .ambient-signals path {
    stroke: rgba(124, 221, 236, 0.34);
    stroke-width: 2.4;
    stroke-dasharray: 12 108;
    animation: ${ambientTrace} 7.8s linear infinite;
    filter: drop-shadow(0 0 8px rgba(124, 221, 236, 0.18));
  }

  .lane-base,
  .lane-flow {
    fill: none;
    stroke-linecap: round;
  }

  .lane-base {
    stroke: rgba(210, 238, 244, 0.16);
    stroke-width: 3;
  }

  .lane-flow {
    stroke: url("#data-platform-lane");
    stroke-width: 4.2;
    stroke-dasharray: 16 84;
    stroke-dashoffset: 0;
    filter: url("#data-platform-soft-glow");
    animation: ${laneMove} 3.9s linear infinite;
  }

  .platform-tile {
    transform-box: fill-box;
    transform-origin: center;
  }

  .tile-face {
    fill: url("#data-platform-tile");
    stroke: rgba(210, 238, 244, 0.26);
    stroke-width: 1.4;
    filter: drop-shadow(0 18px 28px rgba(0, 0, 0, 0.32));
  }

  .tile-topline,
  .tile-edge {
    fill: none;
    stroke-linecap: round;
  }

  .tile-topline {
    stroke: rgba(213, 238, 242, 0.22);
  }

  .tile-edge {
    stroke: rgba(129, 218, 232, 0.45);
    stroke-width: 2;
    stroke-dasharray: 40 54;
    animation: ${edgeScan} 4.8s ease-in-out infinite;
  }

  .tile-edge-b {
    animation-delay: -1.6s;
  }

  .platform-label,
  .route-labels text,
  .core-title,
  .core-subtitle {
    text-anchor: middle;
    font-family: inherit;
    font-weight: 800;
    letter-spacing: 0.16em;
    text-transform: uppercase;
  }

  .platform-label {
    fill: rgba(236, 246, 248, 0.74);
    font-size: 13px;
  }

  .route-labels text {
    fill: rgba(255, 255, 255, 0.26);
    font-size: 11px;
  }

  .platform-icon-line,
  .platform-icon-muted,
  .platform-card {
    fill: none;
    stroke-linecap: round;
    stroke-linejoin: round;
  }

  .platform-icon-line {
    stroke: rgba(223, 241, 244, 0.78);
    stroke-width: 5;
  }

  .platform-icon-line.strong {
    stroke: rgba(214, 245, 250, 0.9);
  }

  .platform-icon-muted {
    stroke: rgba(223, 241, 244, 0.42);
    stroke-width: 4;
  }

  .platform-card {
    stroke: rgba(129, 218, 232, 0.6);
    stroke-width: 3;
  }

  .platform-icon-fill,
  .platform-icon-accent,
  .calendar-cell {
    fill: rgba(209, 235, 240, 0.84);
  }

  .platform-icon-accent,
  .calendar-cell.active {
    fill: rgba(129, 218, 232, 0.92);
    filter: url("#data-platform-soft-glow");
  }

  .platform-icon-muted {
    fill: none;
  }

  .core {
    transform-box: fill-box;
    transform-origin: center;
  }

  .core-aura {
    fill: url("#data-platform-core-glow");
    opacity: 0.56;
    transform-box: fill-box;
    transform-origin: center;
    animation: ${coreBreathe} 4.8s ease-in-out infinite;
  }

  .core-layer {
    fill: rgba(4, 8, 9, 0.66);
    stroke: rgba(159, 229, 239, 0.3);
  }

  .core-layer-3 {
    opacity: 0.34;
  }

  .core-layer-2 {
    opacity: 0.6;
  }

  .core-layer-1,
  .core-face {
    fill: rgba(23, 39, 42, 0.76);
    stroke: rgba(196, 239, 247, 0.42);
    stroke-width: 1.5;
  }

  .core-face {
    fill: rgba(14, 25, 28, 0.78);
  }

  .core-title {
    fill: rgba(226, 244, 248, 0.92);
    font-size: 24px;
    letter-spacing: 0.08em;
  }

  .core-subtitle {
    fill: rgba(120, 215, 231, 0.82);
    font-size: 17px;
  }

  .core-chip-line {
    fill: none;
    stroke: rgba(207, 236, 241, 0.64);
    stroke-width: 3;
    stroke-linecap: round;
  }

  .core-chip-line.soft {
    stroke: rgba(207, 236, 241, 0.22);
  }

  .core-ring {
    fill: none;
    stroke: rgba(111, 211, 229, 0.26);
    stroke-width: 2;
    stroke-linecap: round;
    stroke-dasharray: 14 22;
    transform-box: fill-box;
    transform-origin: center;
    animation: ${coreSpin} 18s linear infinite;
  }

  .core-ring.ring-b {
    opacity: 0.7;
    animation-duration: 28s;
    animation-direction: reverse;
  }

  .core-column {
    stroke: rgba(131, 225, 241, 0.72);
    stroke-width: 2.2;
    stroke-linecap: round;
    stroke-dasharray: 18 50;
    animation: ${columnRise} 3.6s linear infinite;
    filter: url("#data-platform-soft-glow");
  }

  @media (max-width: 720px) {
    border-radius: 20px;
  }

  @media (prefers-reduced-motion: reduce) {
    .lane-flow,
    .tile-edge,
    .ambient-signals path,
    .core-aura,
    .core-ring,
    .core-column {
      animation: none;
    }
  }
`;
